/**
 * ExtensionPasswordBanner · iter v28 r254
 *
 * User M1174: avvisa la PRIMA VOLTA gli utenti Google-OAuth che per usare
 * l'estensione Chrome devono prima impostare una password locale. Una volta
 * impostata (o snoozata), il banner sparisce e non torna più.
 *
 * Mostrato solo se:
 *   - user.auth_provider === "google"
 *   - has_local_password === false
 *   - utente non l'ha già snoozato in localStorage
 *
 * Auto-checks `/api/auth/me-extras` per il flag `has_local_password`.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lightning, X, Key } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { isNative } from "@/lib/native";

const LS_KEY = "pronto_ext_password_banner_dismissed";

export default function ExtensionPasswordBanner({ user }) {
  const [hasPassword, setHasPassword] = useState(null); // null = loading
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      // iter r96b · Su app iOS/Android l'estensione Chrome non esiste,
      // quindi anche il banner "imposta password per l'estensione" è
      // inutile e va nascosto.
      if (isNative) { setDismissed(true); return; }
      if (localStorage.getItem(LS_KEY) === "1") setDismissed(true);
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    // Only Google-OAuth users need this banner — JWT users always have a
    // password by definition.
    if (user?.auth_provider !== "google") {
      setHasPassword(true);
      return;
    }
    let alive = true;
    api.get("/auth/me-extras")
      .then((res) => { if (alive) setHasPassword(!!res.data?.has_local_password); })
      .catch(() => { if (alive) setHasPassword(true); /* fail-safe: hide */ });
    return () => { alive = false; };
  }, [user]);

  if (hasPassword !== false || dismissed) return null;

  const onDismiss = () => {
    try { localStorage.setItem(LS_KEY, "1"); } catch { /* noop */ }
    setDismissed(true);
  };

  return (
    <div
      className="relative bg-[#FFF6E5] border border-[#F2D89E] rounded-md px-5 py-4 mb-6 pr-12"
      data-testid="ext-password-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-[#F2D89E] flex-shrink-0 flex items-center justify-center">
          <Key size={16} weight="bold" className="text-[#8B6914]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-[#1C1C1A] mb-0.5">
            Imposta una password per usare l'estensione Chrome
          </div>
          <p className="text-[12.5px] text-[#6B6A65] leading-relaxed">
            Sei entrato con Google, quindi non hai ancora una password locale.
            L'estensione <strong>Pronto Publisher</strong> (che pubblica i tuoi annunci
            sui portali in 1 click) chiede email + password — è una scelta di sicurezza
            di Chrome, non puoi usare il login Google da lì.
            <br/>
            <span className="text-[#8B6914]">
              <strong>Imposta una password ora</strong> (la usi solo per l'estensione, le altre cose continui a farle con Google).
            </span>
          </p>
          <div className="mt-2.5 flex items-center gap-3 flex-wrap">
            <Link
              to="/dashboard/profile#ext-password-card"
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-white bg-[#1C1C1A] hover:bg-black px-3 py-1.5 rounded-md transition-colors"
              data-testid="ext-password-banner-cta"
            >
              <Lightning size={12} weight="fill" /> Imposta password ora
            </Link>
            <button
              type="button"
              onClick={onDismiss}
              className="text-[11.5px] text-[#9A968B] hover:text-[#1C1C1A] transition-colors"
              data-testid="ext-password-banner-snooze"
            >
              Lo farò dopo
            </button>
          </div>
        </div>
      </div>
      <button
        onClick={onDismiss}
        className="absolute top-2 right-2 w-7 h-7 rounded-full text-[#9A968B] hover:text-[#1C1C1A] hover:bg-white/60 inline-flex items-center justify-center transition-colors"
        aria-label="Chiudi"
        data-testid="ext-password-banner-close"
      >
        <X size={13} weight="bold" />
      </button>
    </div>
  );
}
